import { isDeviceSupportedForTools } from '@/utils/equipment/nmsDetection'
import type { Ref } from 'vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * Minimal interface for device tool state generic constraint
 * Defines the minimum required properties for useDeviceToolState composable
 *
 * Properties are optional to support flexible state objects that may
 * extend this interface with additional required properties.
 *
 * @see {DeviceToolState} from useDeviceTools for the concrete implementation
 */
export interface DeviceToolStateConstraint {
	loading?: boolean
	finished?: boolean
	error?: Error | null
	progress?: number
	cooldownSeconds?: number
}

/**
 * Configuration for device tool card i18n keys
 * (Absorbed from useDeviceToolCard.ts)
 */
export interface DeviceToolCardI18nKeys {
	/** Base i18n prefix (e.g., 'device-tools.status' or 'device-tools.reboot') */
	prefix: string
	/** Key for the "ready" state */
	ready: string
	/** Key for the "in progress" state */
	inProgress: string
	/** Key for the "finishing" state */
	finishing: string
}

export interface UseDeviceToolStateOptions<TState extends DeviceToolStateConstraint, TResult> {
	/** Reactive state from useDeviceTools */
	state: Ref<TState | undefined>

	/** Device NMS type for support checking */
	deviceNms: Ref<string>

	/** Extract result from state */
	resultGetter: (state: TState) => TResult | null

	/** Extract severity from result */
	severityGetter: (result: TResult | null) => string | undefined

	/** i18n keys for status text generation */
	i18nKeys: DeviceToolCardI18nKeys
}

/**
 * Shared state management for device tool components
 * Consolidates duplicated computed properties and state extraction
 *
 * This composable absorbs functionality from:
 * - DeviceStatus.vue lines 245-293 (state + severity computeds)
 * - DeviceReboot.vue lines 259-324 (state + severity + button styling)
 * - useDeviceToolCard.ts (device support + status text)
 *
 * @example
 * const {
 *   isLoading, severity, colors, showResult,
 *   isDeviceSupported, statusText
 * } = useDeviceToolState({
 *   state: computed(() => deviceStatusTests[deviceKey.value]),
 *   deviceNms,
 *   resultGetter: (state) => state.result,
 *   severityGetter: (result) => result?.severity,
 *   i18nKeys: {
 *     prefix: 'device-tools.status',
 *     ready: 'device-tools.status.ready',
 *     inProgress: 'device-tools.status.checking',
 *     finishing: 'device-tools.status.finishing'
 *   }
 * })
 */
export function useDeviceToolState<TState extends DeviceToolStateConstraint, TResult>(
	options: UseDeviceToolStateOptions<TState, TResult>
) {
	const { t } = useI18n()
	const { state, deviceNms, resultGetter, severityGetter, i18nKeys } = options

	// === State extraction ===
	const isLoading = computed(() => state.value?.loading || false)
	const isFinished = computed(() => state.value?.finished || false)
	const hasError = computed(() => !!state.value?.error)
	const progress = computed(() => state.value?.progress || 0)
	const cooldownSeconds = computed(() => state.value?.cooldownSeconds || 0)
	const isInCooldown = computed(() => cooldownSeconds.value > 0)

	// === Result extraction ===
	const result = computed(() => {
		if (!state.value) return null
		return resultGetter(state.value)
	})

	// === Severity computation ===
	const severity = computed(() => severityGetter(result.value) || 'default')

	// === UI state flags ===
	const showCooldownTimer = computed(() => isInCooldown.value && !isLoading.value)
	const showIdleState = computed(() => !isLoading.value && !result.value)
	const showResult = computed(() => isFinished.value && !!result.value)

	// === Device support ===
	/**
	 * Check if the device brand is supported by the backend
	 * Currently supported: Calix and UISP
	 */
	const isDeviceSupported = computed(() => {
		return isDeviceSupportedForTools(deviceNms.value)
	})

	// === Status text ===
	/**
	 * Current status text based on progress and completion state
	 */
	const statusText = computed(() => {
		// Ready state - operation not started
		if (progress.value === 0) {
			return t(i18nKeys.ready)
		}

		// In progress - operation running
		if (progress.value < 100 && !isFinished.value) {
			return t(i18nKeys.inProgress)
		}

		// Finishing - progress complete but finalizing
		if (progress.value >= 100 && !isFinished.value) {
			return t(i18nKeys.finishing)
		}

		// Operation complete
		return ''
	})

	return {
		// State flags
		isLoading,
		isFinished,
		hasError,
		progress,
		cooldownSeconds,
		isInCooldown,

		// Result & severity
		result,
		severity,

		// UI state
		showCooldownTimer,
		showIdleState,
		showResult,

		// Device support & status
		isDeviceSupported,
		statusText
	}
}
